import {ReactNode, useState} from "react";
import classNames from "classnames";
import {getWebpPath} from "@/shared/lib/image/getWebpPath";
import {breakpoints} from "@/shared/config/breakpoints";
import styles from "./Picture.module.scss";

type PictureClasses = {
    root: string;
    image: string;
};

type Props = {
    src: string;
    alt: string;
    srcMobile?: string;
    title?: string;
    width?: number;
    height?: number;
    classes?: Partial<PictureClasses>;
    className?: string;
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'auto' | 'low' | 'high';
    withoutWebp?: boolean;
    onClick?: () => void;
    draggable?: boolean;
    fallbackImage?: ReactNode;
};

export const Picture = (
    {
        src,
        alt,
        srcMobile,
        title,
        width,
        height,
        classes,
        className,
        loading = 'lazy',
        fetchPriority = 'auto',
        withoutWebp,
        onClick,
        draggable = true,
    }: Props
) => {
    const [isError, setIsError] = useState(false);

    if (!src?.trim() || !alt?.trim()) {
        if (import.meta.env.DEV) {
            console.warn(`<Picture /> requires both "src" and "alt" props. Received:`, {src, alt});
        }

        return null;
    }

    if (isError) {
        return (<div className={`load`}></div>);
    }

    const mobile = srcMobile ?? src;
    const desktopMin = breakpoints.mobile + 1;

    const webp = getWebpPath(src, withoutWebp);
    const webpMobile = getWebpPath(mobile, withoutWebp);

    return (
        <picture className={classNames(styles.root, className, classes?.root)}>
            <source type={`image/webp`} srcSet={webpMobile} media={`(max-width: ${breakpoints.mobile}px)`} />
            <source type={`image/webp`} srcSet={webp} media={`(min-width: ${desktopMin}px)`} />
            <source srcSet={mobile} media={`(max-width: ${breakpoints.mobile}px)`} />
            <source srcSet={src} media={`(min-width: ${desktopMin}px)`} />
            <img
                src={src}
                alt={alt}
                title={title ?? alt}
                width={width}
                height={height}
                className={classNames(styles.image, classes?.image)}
                loading={loading}
                // fetchpriority={fetchPriority}
                {...{fetchpriority: fetchPriority}} //TODO убрать после обновления до 19 версии
                draggable={draggable}
                onClick={onClick}
                onError={() => setIsError(true)}
            />
        </picture>
    );
};
